import React from "react";
import { Box, Typography, Modal } from "@mui/material";
import { styled } from "@mui/system";
import { CheckCircle } from "@mui/icons-material";
import { Disclosure, DisclosureStatus, DisclosureSet } from "./disclosure.types";
import { AcceptButton } from "./AcceptButton";
import ArrowRight from "src/icons/ArrowRight";

interface DisclosureListModalProps {
  open: boolean;
  onClose: () => void;
  disclosureSet: DisclosureSet;
  acceptedDisclosures: Record<string, DisclosureStatus>;
  onDisclosureClick: (disclosure: Disclosure) => void;
  onContinue: () => void;
  isComplete: boolean;
}

const ModalContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  padding: "40px",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
  borderRadius: "28px",
  background: "#FFF",
  boxShadow: "0 4px 20px 0 rgba(173, 192, 203, 0.30)",
  outline: "none",
  overflow: "hidden",
  // Default (mobile)
  width: "95%",
  maxWidth: "518px",
  height: "auto",
  maxHeight: "90vh",
  // Tablet (768px+)
  "@media (min-width: 768px)": {
    width: "90%",
    maxWidth: "518px",
    height: "auto",
    maxHeight: "85vh",
  },
  // Desktop (1024px+)
  "@media (min-width: 1024px)": {
    width: "518px",
    height: "444px",
    maxHeight: "444px",
  },
});

const Title = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "20px",
  fontWeight: 500,
  letterSpacing: "-0.48px",
  lineHeight: "28px",
  color: "#002035",
  textAlign: "center",
  "@media (min-width: 768px)": {
    fontSize: "24px",
  },
});

const Subtitle = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "22px",
  color: "#6B7280",
  textAlign: "center",
  "@media (min-width: 768px)": {
    fontSize: "18px",
  },
});

const DisclosureList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
  overflowY: "auto",
  minHeight: 0, // Allows flex child to shrink below content size
  width: "100%",
});

const DisclosureItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAccepted",
})<{ isAccepted: boolean }>(({ isAccepted }) => ({
  display: "flex",
  padding: "24px",
  alignItems: "center",
  gap: "12px",
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "22px",
  borderRadius: "12px",
  border: `1px solid ${isAccepted ? "#10B981" : "#E5E7EB"}`,
  backgroundColor: isAccepted ? "#F0FDF4" : "#FFFFFF",
  cursor: isAccepted ? "default" : "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: isAccepted ? "#10B981" : "#D1D5DB",
    backgroundColor: isAccepted ? "#F0FDF4" : "#F9FAFB",
  },
}));

const DisclosureName = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "18px",
  fontWeight: 400,
  lineHeight: "22px",
  color: "#002035",
  flex: 1,
});

const IconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const DisclosureListModal: React.FC<DisclosureListModalProps> = ({
  open,
  onClose,
  disclosureSet,
  acceptedDisclosures,
  onDisclosureClick,
  onContinue,
  isComplete,
}) => {
  const allAccepted = disclosureSet.disclosures.every(
    (d) => acceptedDisclosures[d.id] === "accepted",
  );

  const getTitle = () => {
    if (isComplete) {
      return disclosureSet.completionTitle;
    }
    return "Here's what you need to know.";
  };

  const getSubtitle = () => {
    if (isComplete) {
      return disclosureSet.completionSubtitle || "";
    }
    return "Please read & acknowledge the following to proceed";
  };

  const getButtonText = () => {
    if (isComplete || allAccepted) {
      return disclosureSet.completionButtonText;
    }
    return "Continue";
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Title>{getTitle()}</Title>
        {getSubtitle() && <Subtitle>{getSubtitle()}</Subtitle>}

        <DisclosureList>
          {disclosureSet.disclosures.map((disclosure) => {
            const isAccepted = acceptedDisclosures[disclosure.id] === "accepted";
            return (
              <DisclosureItem
                key={disclosure.id}
                isAccepted={isAccepted}
                onClick={() => !isAccepted && onDisclosureClick(disclosure)}>
                <DisclosureName>{disclosure.name}</DisclosureName>
                <IconWrapper>
                  {isAccepted ? (
                    <CheckCircle sx={{ color: "#10B981", fontSize: "22px" }} />
                  ) : (
                    <ArrowRight />
                  )}
                </IconWrapper>
              </DisclosureItem>
            );
          })}
        </DisclosureList>

        <AcceptButton
          onClick={allAccepted ? onContinue : undefined}
          disabled={!allAccepted}
          fullWidth>
          {getButtonText()}
        </AcceptButton>
      </ModalContainer>
    </Modal>
  );
};

export default DisclosureListModal;
